import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastrMsgService } from './toastr.service';

export interface ErrorMessage {
  title: string;
  message: string;
  type: 'error' | 'warning' | 'info';
}

@Injectable({
  providedIn: 'root'
})
export class HttpErrorHandlerService {

  constructor(
    private toastrService: ToastrMsgService,
    private router: Router
  ) { }

  /**
   * Handle HTTP errors and provide appropriate user feedback
   * @param error - The HTTP error response
   * @param customMessage - Optional custom message to override default
   */
  handleError(error: HttpErrorResponse, customMessage?: string): void {
    console.error('HTTP Error:', error);

    const errorInfo = this.getErrorMessage(error, customMessage);
    
    // Show appropriate toast notification
    switch (errorInfo.type) {
      case 'error':
        // this.toastrService.error(errorInfo.message);
        break;
      case 'warning':
        // this.toastrService.warning(errorInfo.message);
        break;
      case 'info':
        // this.toastrService.info(errorInfo.message);
        break;
    }

    // Handle specific error codes that require special actions
    this.handleSpecialErrorCodes(error);
  }

  /**
   * Get appropriate error message based on HTTP status code
   */
  private getErrorMessage(error: HttpErrorResponse, customMessage?: string): ErrorMessage {
    if (customMessage) {
      return {
        title: 'Error',
        message: customMessage,
        type: 'error'
      };
    }

    switch (error.status) {
      // Client Errors (4xx)
      case 400:
        return {
          title: 'Bad Request',
          message: 'The request was invalid. Please check your input and try again.',
          type: 'error'
        };

      case 401:
        return {
          title: 'Unauthorized',
          message: 'You are not authorized to access this resource. Please log in again.',
          type: 'error'
        };

      case 403:
        return {
          title: 'Forbidden',
          message: 'You do not have permission to access this resource.',
          type: 'error'
        };

      case 404:
        return {
          title: 'Not Found',
          message: 'The requested resource was not found.',
          type: 'warning'
        };

      case 405:
        return {
          title: 'Method Not Allowed',
          message: 'The HTTP method is not allowed for this resource.',
          type: 'error'
        };

      case 409:
        return {
          title: 'Conflict',
          message: 'The request conflicts with the current state of the resource.',
          type: 'warning'
        };

      case 422:
        return {
          title: 'Validation Error',
          message: 'The request data is invalid. Please check your input.',
          type: 'error'
        };

      case 429:
        return {
          title: 'Too Many Requests',
          message: 'You have exceeded the rate limit. Please try again later.',
          type: 'warning'
        };

      // Server Errors (5xx)
      case 500:
        return {
          title: 'Internal Server Error',
          message: 'An unexpected error occurred. Please try again later.',
          type: 'error'
        };

      case 502:
        return {
          title: 'Bad Gateway',
          message: 'The server received an invalid response from an upstream server.',
          type: 'error'
        };

      case 503:
        return {
          title: 'Service Unavailable',
          message: 'The service is temporarily unavailable. Please try again later.',
          type: 'warning'
        };

      case 504:
        return {
          title: 'Gateway Timeout',
          message: 'The server did not receive a timely response from an upstream server.',
          type: 'error'
        };

      // Network/Connection Errors
      case 0:
        return {
          title: 'Network Error',
          message: 'Unable to connect to the server. Please check your internet connection.',
          type: 'error'
        };

      default:
        return {
          title: 'Error',
          message: error.error?.message || error.message || 'An unexpected error occurred.',
          type: 'error'
        };
    }
  }

  /**
   * Handle special error codes that require specific actions
   */
  private handleSpecialErrorCodes(error: HttpErrorResponse): void {
    switch (error.status) {
      case 401:
        // Unauthorized - redirect to login
        this.handleUnauthorizedError();
        break;

      case 403:
        // Forbidden - redirect to home or show access denied
        this.handleForbiddenError(error);
        break;

      case 404:
        // Not Found - could redirect to 404 page
        this.handleNotFoundError();
        break;

      case 500:
        // Server error - could log to monitoring service
        this.handleServerError();
        break;
    }
  }

  /**
   * Handle 401 Unauthorized errors
   */
  private handleUnauthorizedError(): void {
    // Clear user data from localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    
    // Redirect to login page
    this.router.navigate(['/login']);
  }

  /**
   * Handle 403 Forbidden errors
   * Auth middleware failures (invalid user type, offboarded) must clear session; otherwise
   * login auto-redirect (checkLogIn → home/dashboard) loops with repeated failing API calls.
   */
  private handleForbiddenError(error: HttpErrorResponse): void {
    const body = error.error as { error?: string; message?: string } | undefined;
    const combined = `${body?.error || ''} ${body?.message || ''}`.toLowerCase();
    const isAuthIdentityFailure =
      combined.includes('invalid user type') ||
      combined.includes('offboarded') ||
      combined.includes('account offboarded');

    if (isAuthIdentityFailure) {
      this.handleUnauthorizedError();
      return;
    }
    this.router.navigate(['/']);
  }

  /**
   * Handle 404 Not Found errors
   */
  private handleNotFoundError(): void {
    // Could redirect to 404 page
    // this.router.navigate(['/404']);
  }

  /**
   * Handle 500 Server errors
   */
  private handleServerError(): void {
    // Could log to monitoring service or analytics
    console.error('Server Error - Consider logging to monitoring service');
  }

  /**
   * Handle validation errors from server response
   */
  handleValidationErrors(error: HttpErrorResponse): void {
    if (error.error && error.error.errors) {
      // Handle multiple validation errors
      const validationErrors = error.error.errors;
      if (Array.isArray(validationErrors)) {
        validationErrors.forEach((err: any) => {
          this.toastrService.error(err.message || 'Validation error');
        });
      } else {
        // Handle object-based validation errors
        Object.keys(validationErrors).forEach(key => {
          this.toastrService.error(`${key}: ${validationErrors[key]}`);
        });
      }
    } else {
      // Fallback to general error handling
      this.handleError(error);
    }
  }

  /**
   * Handle timeout errors
   */
  handleTimeoutError(): void {
    this.toastrService.error('Request timed out. Please try again.');
  }

  /**
   * Handle network errors
   */
  handleNetworkError(): void {
    this.toastrService.error('Network error. Please check your internet connection and try again.');
  }
}

